import React from 'react'
import { useNavigate } from 'react-router-dom'
import ProfileSection from 'app/common/pixel-menu/_comp/ProfileSection'
import {
  DcBreadcrumbs,
  DcDiv,
  DcFlexBox,
  PixelPageContainer,
  PixelTooltip,
  Section,
  SectionActions,
  SectionContent,
  colors,
} from 'pixel-base'
import styled from 'styled-components'
import { ChevronRightIcon } from 'utils/icons/chevron-right-icon'
import { ClockIcon } from 'utils/icons/clock-icon'
import { CompletedIcon } from 'utils/icons/completed-icon'
import { ExchangeIcon } from 'utils/icons/exchange-icon'
import { IncomingArrowIcon } from 'utils/icons/incoming-arrow-icon'
import { MoneyCheckIcon } from 'utils/icons/money-check-icon'

const breadcrumbs = [
  {
    name: 'Orders',
    active: false,
    link: '/orders',
  },
  {
    name: 'Returns Dashboard',
    active: true,
  },
]

const palette = {
  text: '#101A33',
  textMuted: '#5D6B85',
  cardBorder: '#E5EAF2',
  expectedIcon: '#1769D1',
  expectedBg: '#E5F0FF',
  receivedIcon: '#159447',
  receivedBg: '#E5F8EE',
  exchangeIcon: '#5135B5',
  exchangeBg: '#F0EAFE',
  refundIcon: '#E99A20',
  refundBg: '#FFF3DF',
  badgeBg: '#F0F2F7',
  badgeText: '#52617A',
}

const returnTypes = [
  {
    title: 'Expected Returns',
    description: 'Track and manage returns expected to arrive.',
    url: '/orders/returns/returning',
    comingSoon: false,
    Icon: IncomingArrowIcon,
    iconColor: palette.expectedIcon,
    iconBackground: palette.expectedBg,
  },
  {
    title: 'Received Returns',
    description: 'Manage returns received at your warehouse.',
    url: '/orders/returns/received',
    comingSoon: false,
    Icon: CompletedIcon,
    iconColor: palette.receivedIcon,
    iconBackground: palette.receivedBg,
  },
  {
    title: 'Exchange Returns',
    description: 'Manage product exchanges.',
    url: null,
    comingSoon: true,
    Icon: ExchangeIcon,
    iconColor: palette.exchangeIcon,
    iconBackground: palette.exchangeBg,
  },
  {
    title: 'Refund Returns',
    description: 'Manage customer refunds.',
    url: null,
    comingSoon: true,
    Icon: MoneyCheckIcon,
    iconColor: palette.refundIcon,
    iconBackground: palette.refundBg,
  },
]

const Returns = () => {
  const navigate = useNavigate()

  return (
    <DcFlexBox flexDirection="column">
      <PixelPageContainer
        backgroundColor={colors.white}
        style={{ padding: '0px' }}
      >
        <Section style={{ padding: '0px' }}>
          <SectionContent style={{ padding: '0px', borderRadius: '0px' }}>
            <DcFlexBox
              padding="0px 28px"
              height="60px"
              style={{
                justifyContent: 'space-between',
                borderBottom: `1px solid ${colors.grayBorderLight}`,
              }}
              alignItems="center"
            >
              <DcFlexBox alignItems="center" width="auto">
                <BreadcrumbWrap>
                  <DcBreadcrumbs fontSize="13px" data={breadcrumbs} />
                </BreadcrumbWrap>
                <SectionActions />
              </DcFlexBox>
              <DcDiv>
                <ProfileSection />
              </DcDiv>
            </DcFlexBox>
          </SectionContent>
        </Section>
        <Section style={{ padding: '20px 28px' }}>
          <SectionContent
            style={{
              padding: '0px',
              background: 'transparent',
              boxShadow: 'none',
              borderRadius: '0px',
            }}
          >
            <PageHeader>
              <PageTitle>Returns</PageTitle>
              <PageSubtitle>Manage your complete returns workflow</PageSubtitle>
            </PageHeader>
            <CardsGrid>
              {returnTypes.map((item) => {
                const card = (
                  <ReturnCard
                    key={item.title}
                    $comingSoon={item.comingSoon}
                    onClick={(event) => {
                      if (item.comingSoon || !item.url) {
                        event.preventDefault()
                        event.stopPropagation()
                        return
                      }
                      navigate(item.url)
                    }}
                    onKeyDown={(event) => {
                      if (item.comingSoon || !item.url) {
                        event.preventDefault()
                        return
                      }
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault()
                        navigate(item.url)
                      }
                    }}
                    role={item.comingSoon ? 'group' : 'link'}
                    tabIndex={item.comingSoon ? -1 : 0}
                    aria-disabled={item.comingSoon}
                    aria-label={
                      item.comingSoon
                        ? `${item.title}. This module is coming soon.`
                        : item.title
                    }
                  >
                    <CardTop>
                      <IconWrap
                        $background={item.iconBackground}
                        $color={item.iconColor}
                        $muted={item.comingSoon}
                      >
                        <item.Icon size={20} color="currentColor" />
                      </IconWrap>
                      {item.comingSoon ? (
                        <ComingSoonBadge>
                          <ClockIcon size={18} color="currentColor" />
                          Coming Soon
                        </ComingSoonBadge>
                      ) : (
                        <ArrowWrap aria-hidden="true">
                          <ChevronRightIcon size={16} color="currentColor" />
                        </ArrowWrap>
                      )}
                    </CardTop>
                    <CardTitle>{item.title}</CardTitle>
                    <CardDescription>{item.description}</CardDescription>
                  </ReturnCard>
                )

                if (!item.comingSoon) return card

                return (
                  <PixelTooltip
                    key={item.title}
                    tooltipText="This module is coming soon."
                    tooltipPlacement="top"
                    width="100%"
                    height="100%"
                    style={{ cursor: 'not-allowed' }}
                  >
                    {card}
                  </PixelTooltip>
                )
              })}
            </CardsGrid>
          </SectionContent>
        </Section>
      </PixelPageContainer>
    </DcFlexBox>
  )
}

const BreadcrumbWrap = styled.div`
  .breadcrumb {
    margin-bottom: 0;
    padding: 0;
    background: transparent;
  }

  .breadcrumb-item a {
    font-size: 13px !important;
    font-weight: 500;
    color: ${palette.textMuted} !important;
  }

  .breadcrumb-item.active {
    font-size: 13px !important;
    font-weight: 600;
    color: ${palette.text} !important;
  }

  .breadcrumb-item + .breadcrumb-item::before {
    opacity: 0.35;
  }
`

const PageHeader = styled.div`
  margin-bottom: 36px;
`

const PageTitle = styled.h1`
  margin: 0 0 6px;
  font-size: 32px;
  font-weight: 700;
  line-height: 1.2;
  letter-spacing: -0.02em;
  color: ${palette.text};

  @media (max-width: 768px) {
    font-size: 28px;
  }
`

const PageSubtitle = styled.p`
  margin: 0;
  font-size: 15px;
  font-weight: 400;
  line-height: 1.5;
  color: ${palette.textMuted};
`

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 20px;
  align-items: stretch;

  > * {
    min-width: 0;
    height: 100%;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`

const ArrowWrap = styled.span`
  display: inline-flex;
  color: ${palette.textMuted};
  transition: transform 0.16s ease;
`

const ReturnCard = styled.div<{ $comingSoon: boolean }>`
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 16px;
  background: ${(props) => (props.$comingSoon ? '#F8F9FC' : colors.white)};
  border: 1px solid
    ${(props) => (props.$comingSoon ? '#E8EDF4' : palette.cardBorder)};
  border-radius: 16px;
  box-shadow: ${(props) =>
    props.$comingSoon ? 'none' : '0 1px 3px rgba(16, 26, 51, 0.04)'};
  cursor: ${(props) => (props.$comingSoon ? 'not-allowed' : 'pointer')};
  transition: border-color 0.16s ease, box-shadow 0.16s ease,
    transform 0.16s ease;

  ${(props) =>
    !props.$comingSoon &&
    `
    &:hover,
    &:focus-visible {
      border-color: #d5ddea;
      box-shadow: 0 4px 12px rgba(16, 26, 51, 0.08);
      transform: translateY(-2px);
      outline: none;

      ${ArrowWrap} {
        transform: translateX(3px);
      }
    }
  `}
`

const CardTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 42px;
  margin-bottom: 12px;
`

const IconWrap = styled.span<{
  $background: string
  $color: string
  $muted: boolean
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 42px;
  height: 42px;
  border-radius: 50%;
  background: ${(props) => props.$background};
  color: ${(props) => props.$color};
  opacity: ${(props) => (props.$muted ? 0.72 : 1)};

  svg {
    display: block;
    flex-shrink: 0;
  }
`

const ComingSoonBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  border-radius: 20px;
  background: ${palette.badgeBg};
  color: ${palette.badgeText};
  font-size: 13px;
  font-weight: 600;
  line-height: 1.2;
  white-space: nowrap;
`

const CardTitle = styled.h2`
  margin: 0 0 6px;
  font-size: 19px;
  font-weight: 600;
  line-height: 1.35;
  letter-spacing: -0.01em;
  color: ${palette.text};
  overflow-wrap: break-word;
`

const CardDescription = styled.p`
  margin: 0;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.5;
  color: ${palette.textMuted};
  overflow-wrap: break-word;
`

export default Returns
